import store from '@/store';
import { TCreativeMultipleFields, TCropCoordinate, TDictionary } from '@/types';
import { ICreativeStatistics } from '@/types/campaign/creatives';
import {
  getCropAspectRatio,
  getCropCoordinates,
  STORE_PATH,
} from '@/configs/creativeConfigs';

export const getShowcaseStatistics = (
  creativeStats: Array<ICreativeStatistics>,
  creativeIds: Array<string>
) => {
  try {
    const showcaseCreativeStatistics = creativeStats.filter((creative) =>
      creativeIds.includes(String(creative.id))
    );

    const impression = showcaseCreativeStatistics.reduce((acc, item) => {
      acc += Number(item.count_view);
      return acc;
    }, 0);

    const order = showcaseCreativeStatistics.reduce((acc, item) => {
      acc += Number(item.count_lead);
      return acc;
    }, 0);

    const spend = showcaseCreativeStatistics.reduce((acc, item) => {
      acc += Number(item.sum_revenue);
      return acc;
    }, 0);

    const clicks = showcaseCreativeStatistics.reduce((acc, item) => {
      acc += Number(item.count_click);
      return acc;
    }, 0);

    const cpc = spend > 0 ? (spend / clicks).toFixed(2) : 0;
    const ctr = clicks > 0 ? ((clicks / impression) * 100).toFixed(2) : 0;

    return {
      impression,
      cpc,
      ctr,
      order,
    };
  } catch (err) {
    console.error('getShowcaseStatistics: ', err);
  }
};

export const getCreativeStorePath = (name: string): string => {
  return `${STORE_PATH}/${name}`;
};

export const checkCreativeHaveErrors = (creativeId: string): boolean => {
  return store.getters[
    getCreativeStorePath('getEditCreativesHaveErrors')
  ].includes(creativeId);
};

export const checkEditCreativesHaveErrors = (): boolean => {
  return (
    store.getters[getCreativeStorePath('getEditCreativesHaveErrors')].length ===
    0
  );
};

export const checkMultipleFieldsHaveErrors = (
  multipleFields: TDictionary<TCreativeMultipleFields>
): boolean => {
  let isValid = true;

  try {
    for (const creativeId in multipleFields) {
      for (const field in multipleFields[creativeId]) {
        for (const item of multipleFields[creativeId][field]) {
          if (item.errors.length) {
            isValid = false;
          }
        }
      }
    }
    if (!checkEditCreativesHaveErrors()) {
      isValid = false;
    }
  } catch (err) {
    console.error('checkMultipleFieldsHaveErrors: ', err);
  }

  return isValid;
};

export const splitAspectRatio = (value: string): Array<number> => {
  return value.split(':').map((v) => Number(v));
};

export const getHeightByAspectRatio = (
  widthValue: number,
  aspectRatio: string
): number => {
  const [width, height] = splitAspectRatio(aspectRatio);
  return Math.floor((widthValue * height) / width);
};

export const createCropCoordinates = (
  image: HTMLImageElement
): Array<TCropCoordinate> => {
  const coordinates = [] as Array<TCropCoordinate>;
  const aspectRatio = getCropAspectRatio();
  let cropCoordinates = {} as TCropCoordinate;

  try {
    for (const ratio of aspectRatio) {
      cropCoordinates = getCropCoordinates();
      const [width, height] = ratio.value.split(':');

      const creativeWidth = Math.floor(
        image.height * (Number(width) / Number(height))
      );
      const creativeHeight = Math.floor(
        image.width * (Number(height) / Number(width))
      );
      const top = Math.floor((image.height - creativeHeight) / 2);
      const left = Math.floor((image.width - creativeWidth) / 2);
      cropCoordinates.aspect_ratio = ratio.value;

      cropCoordinates.top = top > 0 ? top : 0;
      cropCoordinates.left = left > 0 ? left : 0;
      cropCoordinates.width =
        creativeWidth > image.width ? image.width : creativeWidth;
      cropCoordinates.height =
        creativeHeight > image.height ? image.height : creativeHeight;

      coordinates.push(cropCoordinates);
    }
  } catch (err) {
    console.error('getCoordinates: ', err);
  }

  return coordinates;
};
